/**
 * /impact/stories — Success Stories.
 *
 * A curated, editorial reel of member stories. Content is static here
 * (pre-launch collateral) — once a moderator tool for approving
 * member submissions ships, this page becomes fed from that backend.
 */
import { Link } from "react-router-dom";
import { Sparkles, Quote, ArrowRight, MapPin, Heart } from "lucide-react";
import SeoHead from "@/components/SeoHead";

const STORIES = [
  {
    id: "meera-cochin",
    name: "Meera Nair",
    where: "Kochi, India",
    role: "Retired teacher · 3,240 Social Capital",
    category: "Share Knowledge",
    photo: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=600&q=60",
    quote: "I started recording Malayalam folk songs my grandmother taught me. Two years in, my passport has 84 verified recordings and a linguistics professor from Chennai reached out about a joint archive.",
    body: "Meera's contributions started as short WhatsApp voice notes to her grandchildren. She uploaded them, added the lyrics and context, and each one was verified by the local Kerala Folklore Society. Today she runs a Sunday workshop where seniors record their own songs.",
    stats: [
      { label: "Songs recorded", value: "84" },
      { label: "Verified by", value: "Kerala Folklore Society" },
      { label: "Youth learners", value: "160+" },
    ],
  },
  {
    id: "rahul-portland",
    name: "Rahul Iyer",
    where: "Portland, USA",
    role: "Software engineer · 1,410 Social Capital",
    category: "Volunteer & Serve",
    photo: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&w=600&q=60",
    quote: "I thought volunteering would eat my weekends. Turns out logging even four hours a month at the community kitchen gave me the best signal I could send prospective employers about who I am.",
    body: "Rahul spent 2 hours every Saturday cooking at Blanchet House. He kept a private log with photos and shift-sheet signatures. His verified volunteer record ended up in his cover letters — a subtle differentiator he says opened three interviews.",
    stats: [
      { label: "Hours logged", value: "94" },
      { label: "Meals prepared", value: "1,120" },
      { label: "Verified by", value: "Blanchet House" },
    ],
  },
  {
    id: "priya-mumbai",
    name: "Priya Deshmukh",
    where: "Mumbai, India",
    role: "College student · 890 Social Capital",
    category: "Support Farmers",
    photo: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=600&q=60",
    quote: "My passport helped me get into the sustainability fellowship. Not the essays — the log of the 46 farm visits I'd already done, each with a farmer's signature.",
    body: "Priya visited small farms across Maharashtra, documenting their crop-rotation practices and connecting them with buyers via the FarmersPulse network. Every visit was time-stamped and endorsed by the visited farmer.",
    stats: [
      { label: "Farms visited", value: "46" },
      { label: "Sales connections", value: "12" },
      { label: "Verified by", value: "Farmers themselves" },
    ],
  },
  {
    id: "amos-nairobi",
    name: "Amos Wanjiku",
    where: "Nairobi, Kenya",
    role: "Nature-atlas contributor · 2,105 Social Capital",
    category: "Protect Nature",
    photo: "https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=600&q=60",
    quote: "I photograph what I see on my morning runs — birds, plants, insects. A pangolin sighting last winter made the district's rare-species register. That single row is worth more than a hundred small ones.",
    body: "Amos treats his passport like a naturalist's field diary. The system's rarity multiplier gave his verified pangolin sighting an outsized boost, but he insists the small everyday observations matter equally.",
    stats: [
      { label: "Species logged", value: "312" },
      { label: "Rare sightings", value: "8" },
      { label: "Verified by", value: "iNaturalist + district office" },
    ],
  },
];

export default function SocialCapitalStoriesPage() {
  return (
    <div className="max-w-6xl mx-auto px-6 py-10 md:py-14" data-testid="sc-stories-page">
      <SeoHead section="social-capital"
               title="Success Stories · SocialCapital.cc"
               description="Real members describing how they built Social Capital and what changed for them and their communities."
               url="/impact/stories" />

      <header className="mb-10">
        <div className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: "#059669" }}>
          Impact · Stories
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold" style={{ color: "#0f172a" }}>
          The people behind the numbers.
        </h1>
        <p className="mt-4 text-base sm:text-lg opacity-80 max-w-2xl">
          Every entry on someone's Social Capital passport is a small story.
          Here are four we've followed closely.
        </p>
      </header>

      {/* Stories */}
      <div className="space-y-10">
        {STORIES.map((s, idx) => (
          <article
            key={s.id}
            className="grid md:grid-cols-5 gap-6 rounded-2xl border overflow-hidden bg-white"
            style={{ borderColor: "rgba(0,0,0,0.08)" }}
            data-testid={`sc-story-${s.id}`}
          >
            <div className={`md:col-span-2 ${idx % 2 === 1 ? "md:order-2" : ""}`}>
              <img
                src={s.photo}
                alt={s.name}
                loading="lazy"
                className="w-full h-56 md:h-full object-cover"
              />
            </div>
            <div className="md:col-span-3 p-6 sm:p-8 flex flex-col">
              <div className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: "#059669" }}>
                {s.category}
              </div>
              <h2 className="font-display text-2xl sm:text-3xl leading-tight">{s.name}</h2>
              <div className="mt-1 text-sm opacity-75 inline-flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5" style={{ color: "#059669" }} />
                <span>{s.where}</span>
                <span className="opacity-40">·</span>
                <span>{s.role}</span>
              </div>

              <blockquote className="mt-5 rounded-xl p-4 relative"
                          style={{ background: "#F9FAFB" }}>
                <Quote className="absolute -top-2 -left-2 w-5 h-5" style={{ color: "#059669" }} aria-hidden />
                <p className="text-base italic leading-relaxed opacity-90">"{s.quote}"</p>
              </blockquote>

              <p className="mt-4 text-sm opacity-80 leading-relaxed">{s.body}</p>

              <ul className="mt-5 grid grid-cols-3 gap-3">
                {s.stats.map((st) => (
                  <li key={st.label} className="rounded-xl border px-3 py-2 text-center"
                      style={{ borderColor: "rgba(0,0,0,0.06)" }}>
                    <div className="text-lg font-display font-bold tabular-nums" style={{ color: "#0F172A" }}>
                      {st.value}
                    </div>
                    <div className="text-[11px] opacity-70 mt-0.5">{st.label}</div>
                  </li>
                ))}
              </ul>
            </div>
          </article>
        ))}
      </div>

      {/* CTA */}
      <aside
        className="mt-12 rounded-2xl border p-6 flex flex-wrap items-center justify-between gap-4"
        style={{ borderColor: "rgba(5,150,105,0.25)", background: "#ECFDF5" }}
      >
        <div className="max-w-2xl">
          <div className="text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1.5"
               style={{ color: "#059669" }}>
            <Sparkles className="w-3 h-3" /> Have a story?
          </div>
          <div className="font-display text-lg sm:text-xl mt-1">
            Tell us how you're building Social Capital — the best stories become the next spotlight.
          </div>
        </div>
        <Link to="/contact"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white"
              style={{ background: "#059669" }}
              data-testid="sc-stories-cta">
          Share your story <Heart className="w-4 h-4" />
        </Link>
      </aside>
    </div>
  );
}
